import {useState} from 'react'
import {View} from 'react-native'
import {plural} from '@lingui/core/macro'
import {useLingui} from '@lingui/react/macro'

import {useBottomBarOffset} from '#/lib/hooks/useBottomBarOffset'
import type {
  CommonNavigatorParams,
  NativeStackScreenProps,
} from '#/lib/routes/types'
import {sanitizeDisplayName} from '#/lib/strings/display-names'
import {
  type Classroom,
  type ClassroomMember,
  type MemberRole,
  showsParentsCount,
} from '#/state/classrooms'
import {
  ClassroomApiError,
  type ClassroomViewer,
  useClassroomByRouteQuery,
  useClassroomMembersQuery,
  useRemoveMemberMutation,
} from '#/state/queries/classrooms'
import {ErrorScreen} from '#/view/com/util/error/ErrorScreen'
import {List} from '#/view/com/util/List'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Layout from '#/components/Layout'
import {Loader} from '#/components/Loader'
import * as Prompt from '#/components/Prompt'
import * as Toast from '#/components/Toast'
import {Text} from '#/components/Typography'
import {MemberRow, sortMembers} from './components/ClassroomMembers'
import {showClassroomError} from './components/showClassroomError'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'ClassroomMembers'>

type Filter = 'all' | 'teacher' | 'student' | 'parent'

/**
 * A classroom's people, on their own page.
 *
 * The counts on a classroom are the way in, exactly as followers and following
 * are the way into a profile's people: tapping one opens this list already on
 * that seat. The roster comes from the server (`listMembers`), which only shows
 * it to the people in the room.
 */
export function ClassroomMembersScreen({route}: Props) {
  const {t: l} = useLingui()
  const query = useClassroomByRouteQuery({
    owner: route.params.name,
    handle: route.params.rkey,
  })
  const room = query.data

  if (!room) {
    if (query.isLoading) {
      return (
        <Layout.Screen testID="classroomMembersReading">
          <Layout.Content>
            <View style={[a.p_xl, a.align_center]}>
              <Loader size="lg" />
            </View>
          </Layout.Content>
        </Layout.Screen>
      )
    }
    const deleted =
      query.error instanceof ClassroomApiError &&
      query.error.code === 'NotFound'
    return (
      <Layout.Screen testID="classroomMembersNotFound">
        <Layout.Content>
          <ErrorScreen
            title={
              deleted
                ? l`Classroom not found`
                : l`Could not load this classroom`
            }
            message={
              deleted
                ? l`This classroom is no longer available.`
                : l`Check your connection and try again.`
            }
            onPressTryAgain={deleted ? undefined : () => void query.refetch()}
            showHeader
          />
        </Layout.Content>
      </Layout.Screen>
    )
  }

  /*
   * Who is in a classroom is for the people in it: the counts are public, the
   * names behind them are not.
   */
  if (!room.viewer.role) {
    return (
      <Layout.Screen testID="classroomMembersMembersOnly">
        <Layout.Content>
          <ErrorScreen
            title={l`Members only`}
            message={l`Who is in a classroom is for the people in it. Join the classroom to see its members.`}
            showHeader
          />
        </Layout.Content>
      </Layout.Screen>
    )
  }

  return (
    <MembersPage
      classroom={room.classroom}
      viewer={room.viewer}
      initialRole={route.params.role}
    />
  )
}

function MembersPage({
  classroom,
  viewer,
  initialRole,
}: {
  classroom: Classroom
  viewer: ClassroomViewer
  initialRole?: MemberRole
}) {
  const t = useTheme()
  const {t: l, i18n} = useLingui()
  const bottomBarOffset = useBottomBarOffset(100)
  const isOwner = viewer.role === 'admin'

  const [filter, setFilter] = useState<Filter>(
    initialRole && initialRole !== 'admin' ? initialRole : 'all',
  )
  const membersQuery = useClassroomMembersQuery(
    classroom.id,
    filter === 'all' ? undefined : filter,
  )
  const removeMutation = useRemoveMemberMutation(classroom.id)
  const [target, setTarget] = useState<{did: string; name: string} | null>(null)
  const removeControl = Prompt.usePromptControl()

  /* The server puts the owner, as admin, at the top of the first page. */
  const members = sortMembers(
    classroom,
    membersQuery.data?.pages.flatMap(page => page.members) ?? [],
  )
  const counts = classroom.publishedCounts ?? {
    teachers: 0,
    students: 0,
    parents: 0,
  }
  const total = 1 + counts.teachers + counts.students + counts.parents

  /*
   * The owner and moderators may take people out; the owner's own seat never,
   * and a moderator's only by the owner. The server enforces the same.
   */
  const mayRemove = (member: ClassroomMember) =>
    viewer.canManage &&
    member.role !== 'admin' &&
    (!member.isModerator || isOwner)

  const onRemove = (did: string) => {
    const member = members.find(m => m.did === did)
    setTarget({did, name: member?.displayName || `@${member?.handle ?? did}`})
    removeControl.open()
  }

  const onConfirmRemove = () => {
    if (!target) return
    const seat = target
    setTarget(null)
    removeMutation.mutate(
      {did: seat.did},
      {
        onSuccess: () =>
          Toast.show(l`${seat.name} was removed from ${classroom.name}`),
        onError: error => showClassroomError(i18n, error),
      },
    )
  }

  const onEndReached = () => {
    if (membersQuery.hasNextPage && !membersQuery.isFetchingNextPage) {
      void membersQuery.fetchNextPage()
    }
  }

  const renderItem = ({
    item,
    index,
  }: {
    item: ClassroomMember
    index: number
  }) => (
    <View
      style={[
        a.px_lg,
        a.py_lg,
        index !== 0 && a.border_t,
        t.atoms.border_contrast_low,
      ]}>
      <MemberRow
        member={item}
        isSchool={item.did === classroom.ownerDid}
        onRemove={mayRemove(item) ? onRemove : undefined}
        removeLabel={l`Remove from classroom`}
      />
    </View>
  )

  return (
    <Layout.Screen testID="classroomMembersScreen">
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            {sanitizeDisplayName(classroom.name)}
          </Layout.Header.TitleText>
          <Layout.Header.SubtitleText>
            {plural(total, {one: '# member', other: '# members'})}
          </Layout.Header.SubtitleText>
        </Layout.Header.Content>
        <Layout.Header.Slot />
      </Layout.Header.Outer>

      <List
        testID="classroomMembersList"
        data={members}
        renderItem={renderItem}
        keyExtractor={member => member.did}
        contentContainerStyle={{paddingBottom: bottomBarOffset}}
        onEndReached={onEndReached}
        ListHeaderComponent={
          <View style={[a.px_lg, a.pt_lg, a.pb_sm, a.gap_sm]}>
            <View style={[a.flex_row, a.gap_sm, a.flex_wrap]}>
              <Chip
                testID="classroomMembersFilter-all"
                label={l`All`}
                selected={filter === 'all'}
                onPress={() => setFilter('all')}
              />
              <Chip
                testID="classroomMembersFilter-teacher"
                label={plural(counts.teachers, {
                  one: '# teacher',
                  other: '# teachers',
                })}
                selected={filter === 'teacher'}
                onPress={() => setFilter('teacher')}
              />
              <Chip
                testID="classroomMembersFilter-student"
                label={plural(counts.students, {
                  one: '# student',
                  other: '# students',
                })}
                selected={filter === 'student'}
                onPress={() => setFilter('student')}
              />
              {/*
                A room that keeps parents out has no parents to list, so the
                filter is dropped with the count on the room's page - and kept
                when a parent is seated in it anyway.
              */}
              {showsParentsCount(classroom) ? (
                <Chip
                  testID="classroomMembersFilter-parent"
                  label={plural(counts.parents, {
                    one: '# parent',
                    other: '# parents',
                  })}
                  selected={filter === 'parent'}
                  onPress={() => setFilter('parent')}
                />
              ) : null}
            </View>
            {viewer.canManage ? (
              <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
                {l`You run this classroom. Taking someone out takes their parent's seat with them.`}
              </Text>
            ) : null}
          </View>
        }
        ListEmptyComponent={
          membersQuery.isLoading ? (
            <View style={[a.px_lg, a.py_4xl, a.align_center]}>
              <Loader size="lg" />
            </View>
          ) : (
            <View style={[a.px_lg, a.py_4xl, a.align_center]}>
              <Text
                testID="classroomMembersEmpty"
                style={[a.text_md, t.atoms.text_contrast_medium]}>
                {membersQuery.isError
                  ? l`The members could not be loaded. Pull to try again.`
                  : l`Nobody holds this seat yet.`}
              </Text>
            </View>
          )
        }
        ListFooterComponent={
          membersQuery.isFetchingNextPage ? (
            <View style={[a.py_lg, a.align_center]}>
              <Loader />
            </View>
          ) : null
        }
        onRefresh={() => void membersQuery.refetch()}
        refreshing={membersQuery.isRefetching}
      />

      <Prompt.Basic
        control={removeControl}
        title={l`Remove this member?`}
        description={
          target
            ? l`${target.name} will be removed from ${classroom.name}.`
            : undefined
        }
        confirmButtonCta={l`Remove`}
        confirmButtonColor="negative"
        onConfirm={onConfirmRemove}
      />
    </Layout.Screen>
  )
}

function Chip({
  label,
  selected,
  onPress,
  testID,
}: {
  label: string
  selected: boolean
  onPress: () => void
  testID?: string
}) {
  return (
    <Button
      testID={testID}
      label={label}
      size="small"
      color={selected ? 'primary' : 'secondary'}
      onPress={onPress}>
      <ButtonText>{label}</ButtonText>
    </Button>
  )
}
